import { FC, useState } from "react";

import { useTranslator } from "@/i18n/useTranslator";

import { DsButton, DsCamera, DsIcon, DsText } from "../components/ui";
import { classNames } from "../utils/classNames";
import { DriverCameraAddCommentModal } from "./DriverCameraAddCommentModal";

export type CameraPhotoWithCommentType = {
  base64PhotoString: string;
  comment: string;
};

type DriverCameraBasePropsType = {
  onCancelClick: () => void;
  minimumPhotos?: number;
  canAddComment?: boolean;
};

type DriverCameraSinglePhotoPropsType = {
  mode: "single-photo";
  onChoosePhoto: (photo: CameraPhotoWithCommentType) => void;
};

type DriverCameraMultiplePhotosPropsType = {
  mode: "multiple-photos";
  onChoosePhoto: (photo: CameraPhotoWithCommentType[]) => void;
};

type DriverCameraPropsType = DriverCameraBasePropsType &
  (DriverCameraSinglePhotoPropsType | DriverCameraMultiplePhotosPropsType);

const DriverCamera: FC<DriverCameraPropsType> = ({
  mode,
  onChoosePhoto,
  onCancelClick,
  minimumPhotos,
  canAddComment = true,
}) => {
  const { t } = useTranslator();

  const [listOfImages, setListOfImages] = useState<
    CameraPhotoWithCommentType[]
  >([]);
  const [imageTakenBase64, setImageTakenBase64] = useState("");
  const [comment, setComment] = useState("");
  const [isAddingComment, setIsAddingComment] = useState(false);

  const handleOnChoosePhoto = () => {
    if (mode === "single-photo") {
      onChoosePhoto({ base64PhotoString: imageTakenBase64, comment });
    } else {
      const finalListOfImages = [
        ...listOfImages,
        { base64PhotoString: imageTakenBase64, comment },
      ];

      onChoosePhoto(finalListOfImages);
    }

    setImageTakenBase64("");
    setComment("");
  };

  const closeAddCommentModal = () => setIsAddingComment(false);
  const openAddCommentModal = () => setIsAddingComment(true);

  const resetImageTakenBase64 = () => {
    setImageTakenBase64("");
    setComment("");
  };

  const updatePhotoComment = (comment: string) => {
    setComment(comment);
    closeAddCommentModal();
  };

  const onAddPhoto = () => {
    setListOfImages([
      ...listOfImages,
      { base64PhotoString: imageTakenBase64, comment },
    ]);
    resetImageTakenBase64();
  };

  const hasMinimumPhotosRequirement = Boolean(
    minimumPhotos && minimumPhotos > 1,
  );

  // It will consider the current image taken as well
  const photosTaken = listOfImages.length + 1;

  const shouldHideDoneButton = Boolean(
    hasMinimumPhotosRequirement && minimumPhotos && photosTaken < minimumPhotos,
  );

  return (
    <div className="fixed inset-0 h-screen bg-black">
      {imageTakenBase64 ? (
        <>
          <DriverCameraAddCommentModal
            isOpen={isAddingComment}
            value={comment}
            onDismiss={closeAddCommentModal}
            onAddComment={updatePhotoComment}
          />

          <div className="w-full mt-4 h-[85%]">
            <img
              src={imageTakenBase64}
              className="w-full h-3/4 object-contain"
            />

            <div className="h-1/4 flex justify-end flex-col">
              {canAddComment && (
                <div
                  className="flex flex-row items-center justify-center"
                  onClick={openAddCommentModal}>
                  <DsIcon icon="Plus" className="text-sos-yellow" />
                  <DsText
                    variant="button"
                    className="text-sos-yellow my-5 ml-3">
                    {t("driver_camera_component.add_comment")}
                  </DsText>
                </div>
              )}

              <div className="inline-flex w-full gap-3 items-center px-4">
                <DsButton
                  variant="secondary"
                  className="w-full"
                  onClick={resetImageTakenBase64}>
                  {t("driver_camera_component.retake")}
                </DsButton>
                {mode === "multiple-photos" && (
                  <DsButton className="w-full" onClick={onAddPhoto}>
                    {t("driver_camera_component.add_photo")}
                  </DsButton>
                )}
                <DsButton
                  className={classNames("w-full", {
                    hidden: shouldHideDoneButton,
                  })}
                  onClick={handleOnChoosePhoto}>
                  {mode === "multiple-photos"
                    ? t("done")
                    : t("driver_camera_component.use")}
                </DsButton>
              </div>

              {hasMinimumPhotosRequirement && (
                <DsText
                  variant="pageTitle"
                  className="text-white text-center mt-3">
                  {t("driver_camera_component.minimum_photos_taken", {
                    photosTaken,
                    photosRequired: minimumPhotos,
                  })}
                </DsText>
              )}
            </div>
          </div>
        </>
      ) : (
        <DsCamera
          onTakePhoto={setImageTakenBase64}
          onCancelClick={onCancelClick}
        />
      )}
    </div>
  );
};

export { DriverCamera };
